import { type ReactElement, useCallback, useState } from 'react';
import { RadioGroup, useDisclosure } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { ShippingOptionType } from '../../../../bff-api/baskets/model';
import { ShippingMethodId } from '../../../../bff-api/baskets/model/ShippingMethodId';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { useBasketInventory } from '../../../../bff-api/baskets/useBasketInventory';
import { useUpdateProductItem } from '../../../../bff-api/baskets/useUpdateProductItem';
import { useUpdateShippingAddressForShipment } from '../../../../bff-api/baskets/useUpdateShippingAddressForShipment';
import { getDefaultShipment, getShipmentByMethodId } from '../../../../bff-api/utils/basket-utils';
import { toOrderAddress } from '../../../../bff-api/utils/store-utils';
import { BopisStoreSearchDrawer } from '../../../../components/bopis-store-search-drawer';
import { SDDStoreSearchDrawer } from '../../../../components/sdd-store-search-drawer';
import { ZipCodeModal } from '../../../../components/zip-code-modal';
import { useSLTToast } from '../../../../hooks/use-toast/useSLTToast';
import { useAddProductToBag } from '../../../../hooks/useAddProductToBag';
import { useBopisStore } from '../../../../hooks/useBopisStore';
import { useSDDStore } from '../../../../hooks/useSDDStore';
import { useUserZipCode } from '../../../../hooks/useUserZipCode';
import { eddAddressToOrderAddress } from '../../../../utils/address-utils';
import { useCustomPreferenceOrDefault } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { getShippingOptionType } from '../../../../utils/product-item-utils';
import {
    getShippingOptions,
    hasAssociatedExtendProductItem,
    isBopis,
    isHardGood,
    isSDD,
    isShipTo,
} from '../../../../utils/product-utils';
import { CartBopisOption } from './cart-bopis-option';
import { CartSDDOption } from './cart-sdd-option';
import { CartShipToOption } from './cart-ship-to-option';
import type { OrderAddress, ProductItem } from '../../../../bff-api/baskets/model';
import type { DeliveryResponse } from '../../../../bff-api/delivery/model';
import type { Product } from '../../../../bff-api/products/model';
import type { Store } from '../../../../bff-api/stores/model';

const PREFIX_ID = 'cart-delivery-options';

interface Props {
    productItem: ProductItem;
    product?: Product;
}

export const CartDeliveryOptions = ({ productItem, product }: Props): ReactElement | null => {
    const isEnabled = useCustomPreferenceOrDefault(
        CustomPreference.ENABLE_CART_DELIVERY_OPTIONS,
        true,
    );

    if (!isEnabled || !product || !isHardGood(productItem)) {
        return null;
    }

    return <CartDeliveryOptionsLayout productItem={productItem} product={product} />;
};

const CartDeliveryOptionsLayout = ({ productItem, product }: Props): ReactElement | null => {
    const { data: basket } = useBasket();
    const { data: basketInventory, isLoading } = useBasketInventory({
        preferenceForBasketValue: true,
    });
    const addProductToBag = useAddProductToBag({ ignoreInventory: true });
    const updateProductItem = useUpdateProductItem();
    const updateShippingAddressForShipment = useUpdateShippingAddressForShipment();

    const { data: zipCode = '' } = useUserZipCode();
    const { data: bopisStore } = useBopisStore({
        preferenceForBasketValue: true,
    });
    const { data: sddStore } = useSDDStore({
        preferenceForBasketValue: true,
    });

    const intl = useIntl();
    const showToast = useSLTToast();

    const {
        isOpen: isOpenZipCode,
        onOpen: onOpenZipCode,
        onClose: onCloseZipCode,
    } = useDisclosure();
    const {
        isOpen: isOpenStoreSearch,
        onOpen: onOpenStoreSearch,
        onClose: onCloseStoreSearch,
    } = useDisclosure();
    const {
        isOpen: isOpenLocationSearch,
        onOpen: onOpenLocationSearch,
        onClose: onCloseLocationSearch,
    } = useDisclosure();

    const [newShippingOptionType, setNewShippingOptionType] = useState<ShippingOptionType | null>();

    const selectedShippingOption = getShippingOptionType(productItem);

    const isLoadingByType = useCallback(
        (type: ShippingOptionType): boolean =>
            newShippingOptionType === type ||
            (selectedShippingOption === type &&
                (updateProductItem.isLoading || updateShippingAddressForShipment.isLoading)),
        [
            newShippingOptionType,
            selectedShippingOption,
            updateProductItem.isLoading,
            updateShippingAddressForShipment.isLoading,
        ],
    );

    if (!product || !basketInventory) {
        return null;
    }

    const { id: productId } = product;
    const { quantity } = productItem;
    const { inventory } = basketInventory;
    const productInventory = inventory[productId];

    const { shipToOption, bopisOption, sddOption } = getShippingOptions(product);
    const id = `${PREFIX_ID}-${productId}`;

    const onChangeEDD = async (data: DeliveryResponse): Promise<void> => {
        const shipToItem = basket?.productItems?.find(isShipTo);

        if (shipToItem) {
            await updateProductItem.mutateAsync({
                itemId: shipToItem.itemId,
                productId: shipToItem.productId,
                quantity: 0,
                c_finalEDD: data?.edd,
                c_fromStoreId: undefined,
            });

            const shipmentId = getDefaultShipment(basket!)?.shipmentId;

            if (shipmentId) {
                const address = eddAddressToOrderAddress(data);

                await updateShippingAddressForShipment.mutateAsync({
                    shipmentId,
                    address,
                    useAsBilling: false,
                });
            }
        }
    };

    const onChangeBopisStore = async (store: Store): Promise<void> => {
        const bopisItem = basket?.productItems?.find(isBopis);

        if (bopisItem) {
            await updateProductItem.mutateAsync({
                itemId: bopisItem.itemId,
                productId: bopisItem.productId,
                quantity: 0,
                c_storepickupenabled: true,
                c_isSameDayDelivery: false,
                c_fromStoreId: store.id,
                c_finalEDD: undefined,
            });

            const shipmentId = getShipmentByMethodId(basket, ShippingMethodId.BOPIS)?.shipmentId;

            if (shipmentId) {
                const address = toOrderAddress(store)!;

                await updateShippingAddressForShipment.mutateAsync({
                    shipmentId,
                    address,
                    useAsBilling: false,
                });
            }
        }
    };

    const onChangeSDDAddress = async (
        address: OrderAddress,
        store: Store | undefined,
    ): Promise<void> => {
        const sddItem = basket?.productItems?.find(isSDD);

        if (sddItem) {
            await updateProductItem.mutateAsync({
                itemId: sddItem.itemId,
                productId: sddItem.productId,
                quantity: 0,
                c_storepickupenabled: false,
                c_isSameDayDelivery: true,
                c_fromStoreId: store?.id ?? null,
                c_finalEDD: undefined,
            });

            const shipmentId = getShipmentByMethodId(
                basket,
                ShippingMethodId.SAME_DAY_DELIVERY,
            )?.shipmentId;

            if (shipmentId) {
                await updateShippingAddressForShipment.mutateAsync({
                    shipmentId,
                    address,
                    useAsBilling: false,
                });
            }
        }
    };

    const onChangeShippingOption = async (
        shippingOptionType: ShippingOptionType,
    ): Promise<void> => {
        if (addProductToBag.isLoading) {
            return;
        }

        setNewShippingOptionType(shippingOptionType);

        let store;

        if (shippingOptionType === ShippingOptionType.BOPIS) {
            store = bopisStore;
        } else if (shippingOptionType === ShippingOptionType.SDD) {
            store = sddStore;
        }

        try {
            const hasExtendProductItem = hasAssociatedExtendProductItem(
                productItem,
                basket?.productItems ?? [],
            );

            await addProductToBag.mutateAsync({
                source: 'BAG_DELIVERY_OPTIONS',
                product,
                quantity,
                shippingOptionType,
                applyExtend: false,
                store,
            });

            if (hasExtendProductItem) {
                showToast(
                    intl.formatMessage({
                        id: 'warranty.removed.not.available.for.selected.delivery.option',
                        defaultMessage:
                            "Your warranty has been removed because it's not available for the selected delivery option",
                    }),
                );
            }
        } finally {
            setNewShippingOptionType(null);
        }
    };

    const isLoadingEDD = isLoadingByType(ShippingOptionType.SHIP_TO);

    const isLoadingBopis = isLoadingByType(ShippingOptionType.BOPIS);

    const isLoadingSDD = isLoadingByType(ShippingOptionType.SDD);

    return (
        <RadioGroup
            data-testid={id}
            display="flex"
            alignItems="start"
            flexDirection="column"
            flexGrow={1}
            width={{
                sm: 'min-content',
                'lg-sm': '50%',
            }}
            minWidth={{
                sm: '260px',
                md: '170px',
                'lg-sm': 'unset',
            }}
            gap={4}
            value={selectedShippingOption}
            onChange={onChangeShippingOption}
        >
            <CartShipToOption
                id={`${id}-ship-to-option`}
                productItem={productItem}
                option={shipToOption}
                inventory={productInventory}
                zipCode={zipCode}
                onOpenZipCode={onOpenZipCode}
                isLoading={isLoadingEDD}
            />

            <CartBopisOption
                id={`${id}-bopis-option`}
                productItem={productItem}
                option={bopisOption}
                inventory={productInventory}
                bopisStore={bopisStore}
                onOpenStoreSearch={onOpenStoreSearch}
                isLoading={isLoadingBopis}
            />

            <CartSDDOption
                id={`${id}-sdd-option`}
                productItem={productItem}
                option={sddOption}
                inventory={productInventory}
                sddStore={sddStore}
                onOpenLocationSearch={onOpenLocationSearch}
                isLoading={isLoadingSDD}
            />

            <ZipCodeModal
                id={`${id}-zip-code-modal`}
                zipCode={zipCode}
                isOpen={isOpenZipCode}
                onClose={onCloseZipCode}
                onChange={onChangeEDD}
            />

            <BopisStoreSearchDrawer
                id={`${id}-store-search-drawer`}
                productId={productId}
                isOpen={isOpenStoreSearch}
                isLoading={isLoading || updateProductItem.isLoading}
                onClose={onCloseStoreSearch}
                onChangeStore={onChangeBopisStore}
                closeOnPickUpStore
            />

            <SDDStoreSearchDrawer
                id={`${id}-location-search-drawer`}
                productId={productId}
                isOpen={isOpenLocationSearch}
                isLoading={isLoading || updateProductItem.isLoading}
                onClose={onCloseLocationSearch}
                onChangeAddress={onChangeSDDAddress}
                closeOnSuccess
            />
        </RadioGroup>
    );
};

CartDeliveryOptions.defaultProps = {
    product: undefined,
};

CartDeliveryOptionsLayout.defaultProps = {
    product: undefined,
};
